import { AsyncPipe, NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import { combineLatest, take } from 'rxjs';

import { IneligibleReasons } from '../../../common/enums/ineligible-scenarios.enum';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import {
    selectApiCallError,
    selectHasAnyGrantees,
    selectGrantorIsIneligibleFor,
} from '../../store/selectors/account-permissions.selector';
import { ContentUtil } from '../../utils/content/content.util';
import { BreakpointObserverService } from '../../services/breakpoint-observer/breakpoint-observer.service';
import { AdobeLaunchService } from '../../services/adobe-launch/adobe-launch.service';
import { AnalyticsCtaType, CtaNames } from '../../services/adobe-launch/adobe-launch.cta';

@Component({
    selector: 'zci-what-would-you-like-to-do-banner',
    templateUrl: './what-would-you-like-to-do-banner.component.html',
    styleUrls: ['./what-would-you-like-to-do-banner.component.scss'],
    standalone: true,
    imports: [ButtonModule, AsyncPipe, NgIf],
})
export class WhatWouldYouLikeToDoBannerComponent {
    readonly grantAccessRoute = APPLICATION_ROUTES.ADD_USERS_MULTIACCOUNT;
    readonly removeAccessRoute = APPLICATION_ROUTES.REMOVE_USERS_MULTIACCOUNT;
    readonly content = ContentUtil.content?.home?.whatWouldYouLikeToDoBanner;
    readonly isMobileView$ = inject(BreakpointObserverService).isSmallView$;
    private readonly store = inject(Store);
    readonly hasAnyGrantees$ = this.store.select(selectHasAnyGrantees);
    private readonly router = inject(Router);
    private readonly adobeLaunchService = inject(AdobeLaunchService);
    private readonly grantorIneligibleReasons = [IneligibleReasons.WEB_REG_OR_REREG_HOLD];

    onGrantAccessClick(): void {
        this.adobeLaunchService.trackAction({
            location: 'access_permissions',
            ctaName: CtaNames.GRANT_ACCESS,
            ctaType: AnalyticsCtaType.BUTTON,
        });
        combineLatest([
            this.store.select(selectGrantorIsIneligibleFor),
            this.store.select(selectApiCallError),
        ])
            .pipe(take(1))
            .subscribe(([reasons, hasIneligibleError]) => {
                if (hasIneligibleError) {
                    this.router.navigate([`/${APPLICATION_ROUTES.TECH_DIFF_REDIRECT}`]);
                    return;
                }

                const grantorReason = reasons?.find((r) =>
                    this.grantorIneligibleReasons.includes(r),
                );
                if (grantorReason) {
                    this.router.navigate([`/${APPLICATION_ROUTES.INELIGIBLE.PATH}`, grantorReason]);
                } else {
                    this.router.navigate([this.grantAccessRoute]);
                }
            });
    }

    onRemoveAccessClick(): void {
        this.adobeLaunchService.trackAction({
            location: 'access_permissions',
            ctaName: CtaNames.REMOVE_ACCESS,
            ctaType: AnalyticsCtaType.BUTTON,
        });
        this.router.navigate([this.removeAccessRoute]);
    }
}
